import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IErrorDetails, IRequestState } from './interfaces';

import {
  ChangeRanksRequest,
  GetLanguagesRequest,
  GetTrashBinMessagesRequest,
  GetMessagesRequest,
  IChangeMessageRankDto,
  ICreateMessageCopiesRequest,
  ICustomerDto,
  IDepartmentDto,
  IDocTypeDto,
  IExternalClientReplacementGroup,
  IGetDepartmentsResponse,
  IGetExternalClientGroupsResponse,
  IGetLanguagesRequest,
  IGetLanguagesResponse,
  IGetMessagesResponse,
  IGetReplacementVariablesAvailableToMessageRequest,
  IGetReplacementVariablesAvailableToMessageResponse,
  GetReplacementVariablesAvailableToMessageRequest,
  IGetTrashBinMessagesRequest,
  IGetTrashBinMessagesResponse,
  ILanguageDto,
  IMessageDto,
  IReplacementVariableDto,
  ISysPrinDto,
  SysPrinDto,
  IOlmMessage,
  IOlmMessagePermissions,
  GetSecondaryMessagesAvailableForLinkingRequest,
  GetSecondaryMessagesAvailableForLinkingResponse,
  SecondaryMessageInfoForLinking,
  IGetAvailableMessageTypesResponse,
  OlmField,
  IGetDuplicateRankWarningMessageRequest,
  IGetDuplicateRankWarningMessageResponse,
  GetDuplicateRankWarningMessageRequest,
  GetMessageSearchResultResponse,
  GetMessageSearchResultRequest,
  OlmLinkedMessageDataRow,
} from '@/api/client';
import Api from '@/api/helpers';
import { IDocTypesState } from './docTypesSlice';
import { ICustomersState } from './customersSlice';
import { GridCellProps } from '@progress/kendo-react-grid';
import { State } from '@progress/kendo-data-query';

export interface IMessageDtoWithExpand extends IMessageDto {
  expanded?: boolean;
}

export interface IMessageDetailsGrid extends IOlmMessage {
  timeSet: number;
}

export interface IMessagesState {
  listViewMessages: IMessageDtoWithExpand[];
  activeMessages: IRequestState & { messages: IMessageDtoWithExpand[] };
  deletedMessages: IRequestState & { messages: IMessageDto[] };
  changeRanks: IRequestState & { changeRanks: IChangeMessageRankDto[] };
  externalClientGroups: IRequestState & { externalClientGroups: IExternalClientReplacementGroup[] };
  replacementVariables: IRequestState & { replacementVariables: IReplacementVariableDto[] } & { warningMsg: string };
  departments: IRequestState & { departments: IDepartmentDto[] };
  languages: IRequestState & { languages: ILanguageDto[]; activeLanguage: ILanguageDto | null };
  createMessageCopies: IRequestState & { messageCopies: ICreateMessageCopiesRequest[] };
  activeMessage: (IOlmMessage & IOlmMessagePermissions) | null;
  messageTypes: IRequestState & { types: string[] };
  secondaryLinkedMessages: IRequestState & { availableMessages: SecondaryMessageInfoForLinking[] };
  expandedMessagesDetails: { [id: number]: IMessageDetailsGrid } | null;
  failedMessageSave: boolean;
  anyChangesMade: boolean;
  saveMessageAttempted: boolean;
  messageDeletedAction: boolean;
  messageDropdownChanged: boolean;
  messageSearch: IRequestState & {
    keyWord: string;
    searchBy: string;
    userSelections: string[];
    searchResults: IMessageDtoWithExpand[];
    noResult: boolean;
    gridDataState: State;
  };
  expandedSearchMessagesDetails: { [id: number]: IMessageDetailsGrid } | null;
  allLinkedMessages: IRequestState & { messages: OlmLinkedMessageDataRow[] };
}

export interface ISelectivityFieldDropdown {
  criterionKey: number | null;
  field: OlmField;
}

const initialState: IMessagesState = {
  listViewMessages: [],
  activeMessages: { messages: [], isRequestActive: false, errorDetails: { statusCode: null, userMessage: null } },
  externalClientGroups: {
    externalClientGroups: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  deletedMessages: {
    messages: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  replacementVariables: {
    replacementVariables: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
    warningMsg: '',
  },
  departments: {
    departments: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  languages: {
    languages: [],
    activeLanguage: null,
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  changeRanks: {
    changeRanks: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  createMessageCopies: {
    messageCopies: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  messageTypes: {
    types: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  secondaryLinkedMessages: {
    availableMessages: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  allLinkedMessages: {
    messages: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  activeMessage: null,
  expandedMessagesDetails: null,
  failedMessageSave: false,
  anyChangesMade: false,
  saveMessageAttempted: false,
  messageDeletedAction: false,
  messageDropdownChanged: false,
  messageSearch: {
    isRequestActive: false,
    keyWord: '',
    searchBy: '',
    userSelections: [],
    searchResults: [],
    noResult: false,
    gridDataState: { skip: 0, take: 100 },
    errorDetails: { statusCode: null, userMessage: null },
  },
  expandedSearchMessagesDetails: null,
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setActiveLanguage: (state, action: PayloadAction<ILanguageDto>) => {
      state.languages.activeLanguage = action.payload;
    },
    setActiveMessage: (state, action: PayloadAction<(IOlmMessage & IOlmMessagePermissions) | null>) => {
      state.activeMessage = action.payload;
    },
    clearStateVars: (state) => {
      state.secondaryLinkedMessages.availableMessages = [];
    },
    setExpandMessage: (state, action: PayloadAction<GridCellProps>) => {
      if (state.activeMessages) {
        // create a copy of the messages (immutable) and update the expanded attribute of the clicked message
        state.activeMessages.messages = state.activeMessages.messages.map((message) => {
          if (message.primaryKey === action.payload.dataItem.primaryKey) {
            message.expanded = !action.payload.dataItem.expanded;
          }
          return message;
        });
      }
    },
    setExpandedMessageDetails: (state, action: PayloadAction<IOlmMessage | null>) => {
      const { payload: message } = action;

      if (message && message.primaryKey) {
        state.expandedMessagesDetails = {
          ...state.expandedMessagesDetails,
          [message.primaryKey]: { ...message, timeSet: Date.now() },
        };
      }
    },
    clearExpandedMessageDetails: (state) => {
      state.expandedMessagesDetails = null;
    },
    resetReplVarWarnMsg: (state) => {
      state.replacementVariables.warningMsg = '';
    },
    setFailedSaveMessage: (state, action: PayloadAction<boolean>) => {
      state.failedMessageSave = action.payload;
    },
    setAnyChangesMade: (state, action: PayloadAction<boolean>) => {
      state.anyChangesMade = action.payload;
    },
    setSaveMessageAttempted: (state, action: PayloadAction<boolean>) => {
      state.saveMessageAttempted = action.payload;
    },
    setMessageDeletedAction: (state, action: PayloadAction<boolean>) => {
      state.messageDeletedAction = action.payload;
    },
    setMessageDropdownChanged: (state, action: PayloadAction<boolean>) => {
      state.messageDropdownChanged = action.payload;
    },
    setKeyWord: (state, action: PayloadAction<string>) => {
      state.messageSearch.keyWord = action.payload;
    },
    setMessageSearchBy: (state, action: PayloadAction<string>) => {
      state.messageSearch.searchBy = action.payload;
    },
    setUserSelection: (state, action: PayloadAction<string[]>) => {
      state.messageSearch.userSelections = action.payload;
    },
    setGlobalSearchGridDataState: (state, action: PayloadAction<State>) => {
      state.messageSearch.gridDataState = {
        filter: action.payload.filter,
        group: action.payload.group,
        sort: action.payload.sort,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    },
    setExpandSearchMessage: (state, action: PayloadAction<GridCellProps>) => {
      if (state.messageSearch) {
        // create a copy of the messages (immutable) and update the expanded attribute of the clicked message
        state.messageSearch.searchResults = state.messageSearch.searchResults.map((message) => {
          if (message.primaryKey === action.payload.dataItem.primaryKey) {
            message.expanded = !action.payload.dataItem.expanded;
          }
          return message;
        });
      }
    },
    setExpandedSearchMessageDetails: (state, action: PayloadAction<IOlmMessage | null>) => {
      const { payload: message } = action;

      if (message && message.primaryKey) {
        state.expandedSearchMessagesDetails = {
          ...state.expandedSearchMessagesDetails,
          [message.primaryKey]: { ...message, timeSet: Date.now() },
        };
      }
    },
    clearExpandedSearchMessageDetails: (state) => {
      state.expandedSearchMessagesDetails = null;
    },
    setListViewMessages: (state, action: PayloadAction<IMessageDtoWithExpand[]>) => {
      state.listViewMessages = action.payload;
    },
    clearMessageSearchResults: (state) => {
      state.messageSearch.searchResults = [];
      state.messageSearch.noResult = false;
      state.messageSearch.gridDataState = { skip: 0, take: 100 };
      setKeyWord('');
      setUserSelection([]);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMessagesByDocType.pending, (state) => {
      state.activeMessages.isRequestActive = true;
    });
    builder.addCase(fetchMessagesByDocType.fulfilled, (state, action) => {
      state.activeMessages.isRequestActive = false;
      state.activeMessages.messages = action.payload.messages;
    });
    builder.addCase(fetchDeletedMessagesByDocType.pending, (state) => {
      state.deletedMessages.isRequestActive = true;
    });
    builder.addCase(fetchDeletedMessagesByDocType.fulfilled, (state, action) => {
      state.deletedMessages.isRequestActive = false;
      state.deletedMessages.messages = action.payload.messages;
    });
    builder.addCase(fetchExternalClientGroupsBySysPrin.pending, (state) => {
      state.externalClientGroups.isRequestActive = true;
    });
    builder.addCase(fetchExternalClientGroupsBySysPrin.fulfilled, (state, action) => {
      state.externalClientGroups.isRequestActive = false;
      state.externalClientGroups.externalClientGroups = action.payload.externalClientGroups;
    });
    builder.addCase(fetchMessageTypes.pending, (state) => {
      state.messageTypes.isRequestActive = true;
    });
    builder.addCase(fetchMessageTypes.fulfilled, (state, action) => {
      state.messageTypes.isRequestActive = false;
      state.messageTypes.types = action.payload.messageTypes;
    });
    builder.addCase(fetchMessageTypes.rejected, (state) => {
      state.messageTypes.isRequestActive = false;
    });
    builder.addCase(fetchReplacementVariables.pending, (state) => {
      state.replacementVariables.isRequestActive = true;
      state.replacementVariables.warningMsg = '';
    });
    builder.addCase(fetchReplacementVariables.fulfilled, (state, action) => {
      state.replacementVariables.isRequestActive = false;
      state.replacementVariables.replacementVariables = action.payload.replacementVariables;
      state.replacementVariables.warningMsg = action.payload.forbiddenVariablesWarning;
    });
    builder.addCase(fetchReplacementVariables.rejected, (state) => {
      state.replacementVariables.isRequestActive = false;
      state.replacementVariables.warningMsg = '';
    });
    builder.addCase(fetchDepartments.pending, (state) => {
      state.departments.isRequestActive = true;
    });
    builder.addCase(fetchDepartments.fulfilled, (state, action) => {
      state.departments.isRequestActive = false;
      state.departments.departments = action.payload.departments;
    });
    builder.addCase(fetchLanguages.pending, (state) => {
      state.languages.isRequestActive = true;
    });
    builder.addCase(fetchLanguages.fulfilled, (state, action) => {
      state.languages.isRequestActive = false;
      state.languages.languages = action.payload.languages;
    });
    builder.addCase(patchChangeRanks.pending, (state) => {
      state.changeRanks.isRequestActive = true;
    });
    builder.addCase(patchChangeRanks.fulfilled, (state) => {
      state.changeRanks.isRequestActive = false;
    });
    builder.addCase(patchChangeRanks.rejected, (state) => {
      state.changeRanks.isRequestActive = false;
    });
    builder.addCase(fetchSecondaryMessagesAvailableForLinking.pending, (state) => {
      state.secondaryLinkedMessages.isRequestActive = true;
    });
    builder.addCase(fetchSecondaryMessagesAvailableForLinking.fulfilled, (state, action) => {
      state.secondaryLinkedMessages.isRequestActive = false;
      state.secondaryLinkedMessages.availableMessages = action.payload.availableSecondaryMessages;
    });
    builder.addCase(fetchSecondaryMessagesAvailableForLinking.rejected, (state) => {
      state.secondaryLinkedMessages.isRequestActive = false;
    });
    builder.addCase(fetchMessageSearchResults.pending, (state) => {
      state.messageSearch.isRequestActive = true;
    });
    builder.addCase(fetchMessageSearchResults.fulfilled, (state, action) => {
      state.messageSearch.isRequestActive = false;
      state.messageSearch.searchResults = action.payload.searchResults;
      if (action.payload.searchResults.length === 0) {
        state.messageSearch.noResult = true;
      } else {
        state.messageSearch.noResult = false;
      }
    });
    builder.addCase(fetchMessageSearchResults.rejected, (state) => {
      state.messageSearch.isRequestActive = false;
    });
  },
});

export const fetchMessagesByDocType = createAsyncThunk<
  IGetMessagesResponse,
  { activeCustomer: ICustomerDto | null; activeDocType: IDocTypeDto | null },
  { rejectValue: IErrorDetails }
>('messages/fetchMessagesByDocType', async ({ activeCustomer, activeDocType }, thunkApi) => {
  if (activeCustomer?.sysPrin.system && activeCustomer.sysPrin.principle && activeDocType?.primaryKey) {
    const client = Api.getMessagesClient(thunkApi.dispatch);
    const response = await client.getMessages(
      new GetMessagesRequest({
        system: activeCustomer.sysPrin.system,
        principle: activeCustomer.sysPrin.principle,
        docTypePrimaryKey: activeDocType.primaryKey,
      }),
    );

    if (
      ((thunkApi.getState() as any).docTypes as IDocTypesState).activeDocType?.primaryKey !==
        activeDocType.primaryKey ||
      ((thunkApi.getState() as any).customers as ICustomersState).activeCustomer?.sysPrin.sysPrin !==
        activeCustomer.sysPrin.sysPrin
    ) {
      throw new Error('Fetched messages do not correspond to the correct sys prin and doc type.');
    }

    return response;
  }

  return { messages: [] };
});

export const fetchDeletedMessagesByDocType = createAsyncThunk<
  IGetTrashBinMessagesResponse,
  IGetTrashBinMessagesRequest,
  { rejectValue: IErrorDetails }
>('messages/fetchDeletedMessagesByDocType', async (request, thunkApi) => {
  if (request) {
    const client = Api.getMessagesClient(thunkApi.dispatch);
    const response = await client.getTrashBinMessages(new GetTrashBinMessagesRequest(request));

    if (
      ((thunkApi.getState() as any).docTypes as IDocTypesState).activeDocType?.primaryKey !==
        request.docTypePrimaryKey ||
      ((thunkApi.getState() as any).customers as ICustomersState).activeCustomer?.sysPrin.system !== request.system ||
      ((thunkApi.getState() as any).customers as ICustomersState).activeCustomer?.sysPrin.principle !==
        request.principle
    ) {
      throw new Error('Fetched messages do not correspond to the correct sys prin and doc type.');
    }

    return response;
  }

  return { messages: [] };
});

export const fetchExternalClientGroupsBySysPrin = createAsyncThunk<
  IGetExternalClientGroupsResponse,
  ISysPrinDto[],
  { rejectValue: IErrorDetails }
>('messages/fetchExternalClientGroupsBySysPrin', async (selectedSysPrins, thunkApi) => {
  const client = Api.getMessagesClient(thunkApi.dispatch);
  const sysPrinDtos = selectedSysPrins.map((sysPrin) => new SysPrinDto(sysPrin));
  const response = await client.getExternalClientGroups(sysPrinDtos);
  return response;
});

export const fetchMessageTypes = createAsyncThunk<
  IGetAvailableMessageTypesResponse,
  undefined,
  { rejectValue: IErrorDetails }
>('messages/fetchAvailableMessageTypes', async (_, thunkApi) => {
  const client = Api.getMessagesClient(thunkApi.dispatch);
  const response = await client.getAvailableMessageTypes();
  return response;
});

export const fetchReplacementVariables = createAsyncThunk<
  IGetReplacementVariablesAvailableToMessageResponse,
  IGetReplacementVariablesAvailableToMessageRequest,
  { rejectValue: IErrorDetails }
>('messages/fetchReplacementVariables', async (request, thunkApi) => {
  const client = Api.getMessagesClient(thunkApi.dispatch);
  const response = await client.getReplacementVariablesAvailableToMessage(
    new GetReplacementVariablesAvailableToMessageRequest(request),
  );

  return response;
});

export const fetchDepartments = createAsyncThunk<IGetDepartmentsResponse, IDocTypeDto, { rejectValue: IErrorDetails }>(
  'messages/fetchDepartments',
  async (activeDocType, thunkApi) => {
    const client = Api.getMessagesClient(thunkApi.dispatch);
    const response = await client.getDepartments(activeDocType.primaryKey);
    return response;
  },
);

export const fetchLanguages = createAsyncThunk<
  IGetLanguagesResponse,
  IGetLanguagesRequest,
  { rejectValue: IErrorDetails }
>('messages/fetchLanguages', async (request, thunkApi) => {
  let response: IGetLanguagesResponse = { languages: [] };
  if (request?.sysPrins?.length) {
    const client = Api.getMessagesClient(thunkApi.dispatch);
    response = await client.getLanguages(new GetLanguagesRequest(request));
  }
  return response;
});

export const fetchDuplicateRankWarning = createAsyncThunk<
  IGetDuplicateRankWarningMessageResponse,
  IGetDuplicateRankWarningMessageRequest,
  { rejectValue: IErrorDetails }
>('messages/fetchDuplicateRankWarningMessage', async (request, thunkApi) => {
  const client = Api.getMessagesClient(thunkApi.dispatch);
  const response = await client.getDuplicateRankWarningMessage(new GetDuplicateRankWarningMessageRequest(request));
  return response;
});

export const patchChangeRanks = createAsyncThunk<void, ChangeRanksRequest>(
  'message/ChangeRanks',
  async (request, { dispatch }) => {
    const client = Api.getMessagesClient(dispatch);
    await client.changeRanks(request);
  },
);

export const fetchSecondaryMessagesAvailableForLinking = createAsyncThunk<
  GetSecondaryMessagesAvailableForLinkingResponse,
  GetSecondaryMessagesAvailableForLinkingRequest,
  { rejectValue: IErrorDetails }
>('messages/fetchSecondaryMessagesAvailableForLinking', async (request, thunkApi) => {
  const client = Api.getMessagesClient(thunkApi.dispatch);
  const response = await client.getSecondaryMessagesAvailableForLinking(
    GetSecondaryMessagesAvailableForLinkingRequest.fromJS({ ...request }),
  );
  return response;
});

export const fetchMessageSearchResults = createAsyncThunk<
  GetMessageSearchResultResponse,
  GetMessageSearchResultRequest,
  { rejectValue: IErrorDetails }
>('messages/messageSearch', async (request, thunkApi) => {
  const client = Api.getMessagesClient(thunkApi.dispatch);
  const response = await client.getMessageSearchResults(request);
  return response;
});

export const {
  setActiveLanguage,
  setActiveMessage,
  clearStateVars,
  setExpandMessage,
  setExpandedMessageDetails,
  clearExpandedMessageDetails,
  resetReplVarWarnMsg,
  setFailedSaveMessage,
  setAnyChangesMade,
  setSaveMessageAttempted,
  setMessageDeletedAction,
  setMessageDropdownChanged,
  setKeyWord,
  setMessageSearchBy,
  setUserSelection,
  setGlobalSearchGridDataState,
  setExpandSearchMessage,
  setExpandedSearchMessageDetails,
  clearExpandedSearchMessageDetails,
  setListViewMessages,
  clearMessageSearchResults,
} = messagesSlice.actions;

export default messagesSlice.reducer;
